import React, { useEffect, useState } from 'react';
import { Moon, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  const content = type === 'privacy' ? {
    title: 'Política de Privacidad',
    sections: [{
      heading: '1. Información que recopilamos',
      text: 'Recopilamos únicamente la información necesaria para procesar tu compra y entregarte el producto digital "Noches en Paz". Esto incluye: nombre, dirección de correo electrónico y datos de pago procesados de forma segura a través de nuestro proveedor de pagos.'
    }, {
      heading: '2. Uso de la información',
      text: 'Tu información personal se utiliza exclusivamente para: procesar tu pedido y entregarte el producto digital, enviarte comunicaciones relacionadas con tu compra, y mejorar nuestros productos y servicios. No vendemos, alquilamos ni compartimos tu información personal con terceros para fines de marketing.'
    }, {
      heading: '3. Protección de datos',
      text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, pérdida o alteración. Los pagos se procesan a través de plataformas seguras con encriptación SSL.'
    }, {
      heading: '4. Cookies',
      text: 'Nuestro sitio puede utilizar cookies esenciales para el funcionamiento del sitio web y cookies analíticas para entender cómo los visitantes interactúan con nuestro contenido. Puedes configurar tu navegador para rechazar cookies.'
    }, {
      heading: '5. Tus derechos',
      text: 'Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes comunicarte con nosotros a través de los canales indicados en tu confirmación de compra.'
    }, {
      heading: '6. Cambios a esta política',
      text: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página con la fecha de la última actualización.'
    }]
  } : {
    title: 'Términos y Condiciones',
    sections: [{
      heading: '1. Producto digital',
      text: '"Noches en Paz" es un producto digital educativo en formato PDF. Al completar tu compra, recibirás acceso inmediato al material descargable. Este producto es de carácter informativo y educativo.'
    }, {
      heading: '2. Descargo de responsabilidad médica',
      text: 'Este producto NO constituye consejo médico, diagnóstico ni tratamiento. El contenido es educativo y está basado en prácticas generales de higiene del sueño infantil. Si tienes preocupaciones sobre la salud o el sueño de tu bebé, consulta siempre con tu pediatra o profesional de salud.'
    }, {
      heading: '3. Uso personal',
      text: 'El contenido de "Noches en Paz" es para uso personal y familiar únicamente. Queda prohibida la reproducción, distribución, reventa o compartición del material sin autorización expresa por escrito.'
    }, {
      heading: '4. Resultados',
      text: 'Los resultados pueden variar según cada familia y cada bebé. No garantizamos resultados específicos. Las experiencias compartidas son orientativas y no representan una promesa de resultados idénticos para todos los usuarios.'
    }, {
      heading: '5. Política de reembolso',
      text: 'Debido a la naturaleza digital del producto y su entrega inmediata, todas las ventas son finales. Si experimentas problemas técnicos para acceder al material, te ayudaremos a resolverlos.'
    }, {
      heading: '6. Propiedad intelectual',
      text: 'Todo el contenido de "Noches en Paz", incluyendo textos, gráficos, tablas y diseño, está protegido por derechos de autor. El uso no autorizado del material puede resultar en acciones legales.'
    }, {
      heading: '7. Modificaciones',
      text: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. El uso continuado del producto después de cualquier cambio constituye la aceptación de los términos modificados.'
    }]
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-69">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-70" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      y: 30,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} onClick={e => e.stopPropagation()} className="relative w-full max-w-2xl max-h-[80vh] bg-[#111c35] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden" data-id="element-71">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-[#111c35] border-b border-white/5" data-id="element-72">
          <h3 className="text-xl font-serif text-cream" data-id="element-73">{content.title}</h3>
          <button onClick={onClose} className="p-2 rounded-full text-amber-200/50 hover:text-amber-200 hover:bg-white/5 transition-colors" aria-label="Cerrar" data-id="element-74">
            <X size={20} data-id="element-75" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)] space-y-6" data-id="element-76">
          <p className="text-amber-200/40 text-xs" data-id="element-77">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
          {content.sections.map((section, i) => <div key={i} data-id="element-78">
              <h4 className="text-amber-400/90 font-semibold text-sm mb-2" data-id="element-79">
                {section.heading}
              </h4>
              <p className="text-amber-100/60 text-sm leading-relaxed" data-id="element-80">
                {section.text}
              </p>
            </div>)}
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-12 px-4 bg-[#080d19] border-t border-white/5" data-id="element-81">
        <div className="max-w-6xl mx-auto flex flex-col items-center justify-center text-center" data-id="element-82">
          <div className="flex items-center mb-6 text-amber-500/80" data-id="element-83">
            <Moon size={24} className="mr-2" data-id="element-84" />
            <span className="font-serif text-xl tracking-wide" data-id="element-85">
              Noches en Paz
            </span>
          </div>

          <p className="text-amber-100/30 text-sm mb-4" data-id="element-86">
            © {new Date().getFullYear()} Noches en Paz. Todos los derechos
            reservados.
          </p>

          <p className="text-amber-100/20 text-xs max-w-md mb-6" data-id="element-87">
            Este producto es educativo. No reemplaza consejo médico. Si tienes
            dudas sobre la salud de tu bebé, consulta a tu pediatra.
          </p>

          {/* Legal Links */}
          <div className="flex items-center gap-4 text-xs" data-id="element-88">
            <button onClick={() => setActiveModal('privacy')} className="text-amber-200/40 hover:text-amber-400 transition-colors underline underline-offset-2 decoration-amber-200/20 hover:decoration-amber-400/50" data-id="element-89">
              Política de Privacidad
            </button>
            <span className="text-amber-200/20" data-id="element-90">·</span>
            <button onClick={() => setActiveModal('terms')} className="text-amber-200/40 hover:text-amber-400 transition-colors underline underline-offset-2 decoration-amber-200/20 hover:decoration-amber-400/50" data-id="element-91">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-92">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-93" />}
      </AnimatePresence>
    </>;
}